import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

const badgeVariants = cva(
  [
    'inline-flex items-center justify-center rounded-sm px-2 h-5',
    'text-badge',
    'whitespace-nowrap',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-bg-inset text-text-secondary',
        purple: 'bg-accent-100 text-accent-700 dark:bg-accent-800 dark:text-accent-200',
        blue: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-100',
        success: 'bg-success-soft text-success-text',
        warning: 'bg-warning-soft text-warning-text',
        danger: 'bg-danger-soft text-danger-text',
        outline: 'border border-border text-text-secondary',
        // Green, but OUTLINED — and the distinction is load-bearing. Filled `success`
        // means "has history with us"; this is its opposite ("never rented"), and the
        // two are mutually exclusive on any one record, so they can never sit side by
        // side. Outlined vs filled is what stops a glance reading them as the same
        // thing. Border takes the -text colour, not the raw hue: the hue is a graphical
        // mark at >= 3:1 and a 1px border on a dark ground needs the same weight the
        // text does.
        //
        // The green is --chart-3-text, not --success-text: the same hue family a step
        // lighter (#1fbd83 vs #18b158, 19% more relative luminance in dark), which is
        // what Hassan asked for and what a hollow chip needs — a 1px border carries far
        // less colour than a filled ground, so the darker green read as grey at card
        // size. Still a measured pair (5.66:1), not a hand-picked hex.
        successOutline: 'border border-chart-3-text text-chart-3-text',
        // Categorical, NOT semantic. `success`/`warning`/`danger` above answer "is
        // this good or bad"; these answer "which one is it" — channel, source,
        // series — where no answer is better or worse than another. Mixing the two
        // is how a board ends up telling an agent that a WhatsApp lead is a warning.
        // Colours come from the six-series chart palette, which is contrast-measured
        // and separable under all three dichromacies.
        cat1: 'bg-chart-1-soft text-chart-1-text',
        cat2: 'bg-chart-2-soft text-chart-2-text',
        cat3: 'bg-chart-3-soft text-chart-3-text',
        cat4: 'bg-chart-4-soft text-chart-4-text',
        cat5: 'bg-chart-5-soft text-chart-5-text',
        cat6: 'bg-chart-6-soft text-chart-6-text',
        // Neither categorical nor semantic-good/bad: gold means "this one is worth
        // more". Kept out of cat1–cat6 so adding a seventh channel can never
        // accidentally claim it.
        gold: 'bg-gold-soft text-gold-text',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span
      ref={ref}
      // A bare <span> has no role, and ARIA drops aria-label on role-less elements — so a
      // badge whose visible text is a summary ("Spoke 3d ago") and whose aria-label
      // carries the detail ("We spoke to them on 21/08/2026, 14:30") announced only the
      // summary. `role="note"` is the smallest role that makes the label stick without
      // claiming the badge is a control. Applied only when a label is actually present,
      // so ordinary decorative badges stay out of the accessibility tree's way.
      role={props['aria-label'] ? 'note' : undefined}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  ),
);
Badge.displayName = 'Badge';

export { badgeVariants };
